import React, { useState } from "react";
import { api } from "../lib/api";
import { Play, Loader2 } from "lucide-react";

const CATEGORIES = ["Skincare", "Haircare", "Wellness", "Color Cosmetics", "Fragrance", "Bodycare", "Functional Beverages", "Beauty Tools"];
const PACK_SIZES = ["10ml trial", "30ml", "50ml", "100ml", "150ml", "30 gummies", "Set / Multi-pack"];

function MeterRow({ label, value, invert = false, testId }) {
  const good = invert ? value <= 40 : value >= 70;
  const ok = invert ? value <= 65 : value >= 50;
  const color = good ? "bg-bm-green text-bm-green" : ok ? "bg-bm-amber text-bm-amber" : "bg-bm-danger text-bm-danger";
  const [bg, text] = color.split(" ");
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-xs font-mono mb-1">
        <span className="text-bm-muted uppercase tracking-wider">{label}</span>
        <span className={text}>{value}{invert ? "↓" : "↑"}</span>
      </div>
      <div className="h-1.5 bg-white/5">
        <div className={`h-full ${bg}`} style={{ width: `${value}%` }}></div>
      </div>
    </div>
  );
}

export default function Simulation() {
  const [form, setForm] = useState({
    product_name: "New Concept SKU",
    category: "Skincare",
    price_inr: 399,
    pack_size: "30ml",
    geography: "India",
    claims: ["Cooling", "Sweat-proof"],
    ingredients: ["Niacinamide", "Panthenol", "Ectoin"],
    positioning: "Humid-climate hero",
  });
  const [claimInput, setClaimInput] = useState("");
  const [ingInput, setIngInput] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.simulate(form);
      setResult(r);
    } finally { setRunning(false); }
  };

  const addItem = (key, val, clearer) => {
    if (!val.trim()) return;
    set(key, [...form[key], val.trim()]);
    clearer("");
  };
  const removeItem = (key, idx) => set(key, form[key].filter((_, i) => i !== idx));

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4">
        <div className="label-mono text-bm-amber">SIMULATION ENGINE</div>
        <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
          What if we built this.
        </h1>
        <p className="text-sm text-bm-muted mt-2 max-w-2xl">
          Move the levers — price, claims, format, ingredients. The engine predicts repeat, CAC sensitivity, virality, and margin profile.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Inputs */}
        <div className="bm-card p-5 space-y-4" data-testid="sim-inputs">
          <div className="label-mono text-bm-cyan">VARIABLES</div>

          <div>
            <label className="label-mono text-bm-dim">Product Name</label>
            <input value={form.product_name} onChange={(e) => set("product_name", e.target.value)}
              data-testid="sim-name"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono text-bm-dim">Category</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="sim-category"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono text-bm-dim">Pack Size</label>
              <select value={form.pack_size} onChange={(e) => set("pack_size", e.target.value)} data-testid="sim-pack"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none">
                {PACK_SIZES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="label-mono text-bm-dim">Price (INR): <span className="text-bm-amber">₹{form.price_inr}</span></label>
            <input type="range" min={99} max={1499} step={10} value={form.price_inr}
              onChange={(e) => set("price_inr", +e.target.value)} data-testid="sim-price"
              className="w-full accent-bm-amber mt-1" />
          </div>

          <div>
            <label className="label-mono text-bm-dim">Claims</label>
            <div className="flex gap-2 mt-1">
              <input value={claimInput} onChange={(e) => setClaimInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem("claims", claimInput, setClaimInput))}
                placeholder="e.g. 24h hydration"
                data-testid="sim-claim-input"
                className="flex-1 bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm outline-none focus:border-bm-cyan" />
              <button onClick={() => addItem("claims", claimInput, setClaimInput)}
                data-testid="sim-claim-add"
                className="px-3 bg-bm-cyan text-black font-mono text-xs hover:bg-white">ADD</button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {form.claims.map((c, i) => (
                <button key={`claim-${i}-${c}`} onClick={() => removeItem("claims", i)}
                  className="text-[11px] font-mono px-2 py-1 border border-bm-cyan/40 text-bm-cyan hover:bg-bm-cyan hover:text-black">
                  {c} ×
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-mono text-bm-dim">Ingredients</label>
            <div className="flex gap-2 mt-1">
              <input value={ingInput} onChange={(e) => setIngInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem("ingredients", ingInput, setIngInput))}
                placeholder="e.g. Niacinamide"
                data-testid="sim-ing-input"
                className="flex-1 bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm outline-none focus:border-bm-cyan" />
              <button onClick={() => addItem("ingredients", ingInput, setIngInput)}
                data-testid="sim-ing-add"
                className="px-3 bg-bm-amber text-black font-mono text-xs hover:bg-white">ADD</button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {form.ingredients.map((c, i) => (
                <button key={`ing-${i}-${c}`} onClick={() => removeItem("ingredients", i)}
                  className="text-[11px] font-mono px-2 py-1 border border-bm-amber/40 text-bm-amber hover:bg-bm-amber hover:text-black">
                  {c} ×
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-mono text-bm-dim">Positioning</label>
            <input value={form.positioning} onChange={(e) => set("positioning", e.target.value)}
              data-testid="sim-positioning"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm outline-none focus:border-bm-cyan" />
          </div>

          <button onClick={run} disabled={running} data-testid="sim-run"
            className="w-full bg-bm-amber text-black font-heading font-bold py-3 hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {running ? <><Loader2 className="w-4 h-4 animate-spin" />RUNNING SIMULATION...</> : <><Play className="w-4 h-4" />RUN SIMULATION</>}
          </button>
        </div>

        {/* Results */}
        <div className="bm-card p-5 space-y-4" data-testid="sim-results">
          <div className="label-mono text-bm-cyan">PREDICTED OUTCOMES</div>
          {!result && (
            <div className="font-mono text-sm text-bm-muted py-8 text-center">
              Run the simulation to see predicted outcomes.
            </div>
          )}
          {result && (
            <div className="space-y-4">
              <MeterRow label="Repeat Potential" value={result.repeat_potential} testId="meter-repeat" />
              <MeterRow label="CAC Sensitivity (lower = better)" value={result.cac_sensitivity} invert testId="meter-cac" />
              <MeterRow label="Retail Viability" value={result.retail_viability} testId="meter-retail" />
              <MeterRow label="Virality Probability" value={result.virality_probability} testId="meter-virality" />
              <MeterRow label="Margin Profile" value={result.margin_profile} testId="meter-margin" />
              <MeterRow label="Sampling Requirement" value={result.sampling_requirement} invert testId="meter-sampling" />

              <div className="border-t border-white/10 pt-3 mt-4">
                <div className="label-mono text-bm-amber mb-2">SYSTEM REASONING</div>
                <ul className="space-y-1.5 text-sm text-bm-muted">
                  {result.reasoning.map((r, i) => <li key={`reason-${i}-${r.slice(0,20)}`} className="flex gap-2"><span className="text-bm-cyan">→</span>{r}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
